import { Component, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { integrationActions } from '../../actions/integration.actions';
import { ConnectionStatus } from '../../models/integration/connection-status.enum';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { VendorBadgeState } from '../../models/integration/vendor-badge-state.model';
import { VendorDefinition } from '../../models/integration/vendor-definition.model';
import { IntegrationProvider } from '../../provider/integration.provider';
import { apiKeyExpiryState, worstApiKeyExpiryState } from '../../utils/api-key-expiry.util';

@Component({
    selector: 'aumico-integrations-overview',
    templateUrl: './integrations-overview.component.html',
    styleUrls: ['./integrations-overview.component.scss'],
})
export class IntegrationsOverviewComponent implements OnInit, OnDestroy {
    // Org Settings shows ALL vendors. Per-org vendors (BC) get the connect/manage
    // flow; per-company vendors (Bexio) get an info-modal pointing the user to the
    // individual company (scope-based branch in onInfo).
    public readonly vendors = VENDOR_DEFINITIONS;
    public connections$!: Observable<IIntegrationConnection[]>;
    public pendingSetup$ = this.integrationProvider.getPendingSetup$();
    public singleEditActive$ = this.integrationProvider.isSingleEditActive$();

    /**
     * Per-vendor badge state. Keyed by vendor enum. Built once in ngOnInit so the
     * async pipe binds to a stable observable reference (no new observable per CD cycle).
     */
    public vendorStates: Record<string, Observable<VendorBadgeState>> = {};

    /** Local UI state: which vendor's connect-modal is open. */
    public activeConnectVendor: IntegrationVendor | null = null;
    /** Local UI state: which vendor's manage-modal is open. */
    public activeManageVendor: IntegrationVendor | null = null;
    /** Local UI state: which per-company vendor's redirect-info modal is open (e.g. Bexio). */
    public activeRedirectVendor: VendorDefinition | null = null;
    /**
     * When the post Connect orchestration fires `connectionJustCreated`, we set
     * both `activeManageVendor` (open the modal) and this id (drill in directly
     * to the detail view of the new tenant). Reset on manage modal close.
     */
    public manageModalInitialConnectionId: string | null = null;

    /**
     * Abacus mapping modal state. Parent-controlled (no NgRx slice): the manage
     * modal emits `requestEditMapping` with the connectionId + aumicoCompanyId
     * (null when adding a new mapping) and we render `<aumico-abacus-mapping-modal>`
     * only while this is non-null.
     */
    public abacusMapping: { connectionId: string; aumicoCompanyId: string | null } | null = null;

    public readonly IntegrationVendor = IntegrationVendor;

    private readonly destroy$ = new Subject<void>();

    public constructor(
        public readonly integrationProvider: IntegrationProvider,
        private readonly actions$: Actions,
    ) {}

    public ngOnInit(): void {
        this.connections$ = this.integrationProvider.getConnections$();
        this.integrationProvider.loadConnections();

        // Build the per-vendor badge-state streams once.
        for (const vendor of this.vendors) {
            this.vendorStates[vendor.vendor] = this.integrationProvider
                .getConnectionsByVendor$(vendor.vendor)
                .pipe(map((connections) => this.deriveCardState(vendor.vendor, connections)));
        }

        // Listen for the post Connect signal: the diffing effect emitted
        // `connectionJustCreated` because a fresh connection was added. We
        // auto open the manage modal directly into the detail view so the
        // user lands on the result without an extra click.
        //
        // The action carries the vendor so we pick the right manage modal
        // (D365 BC vs Abacus) per the *ngIf branches in the template.
        this.actions$
            .pipe(ofType(integrationActions.connectionJustCreated), takeUntil(this.destroy$))
            .subscribe(({ connectionId, vendor }) => {
                this.manageModalInitialConnectionId = connectionId;
                this.activeManageVendor = vendor;
            });

        // Abacus connect hit a 409 (server already connected in the org). The
        // effect fired the toast + reset pendingSetup; here we close the connect
        // modal and (re)open the Abacus manage modal on its server LIST view
        // (no initialConnectionId → list, not a tenant detail).
        this.actions$
            .pipe(ofType(integrationActions.abacusConnectAlreadyExists), takeUntil(this.destroy$))
            .subscribe(() => {
                this.activeConnectVendor = null;
                this.manageModalInitialConnectionId = null;
                this.activeManageVendor = IntegrationVendor.ABACUS;
            });

        // Topal connect hit a 409 (server already connected in the org). Close the
        // connect modal and reopen the Topal manage modal on its server LIST view
        // (mirrors the Abacus already-exists choreography).
        this.actions$
            .pipe(ofType(integrationActions.topalConnectAlreadyExists), takeUntil(this.destroy$))
            .subscribe(() => {
                this.activeConnectVendor = null;
                this.manageModalInitialConnectionId = null;
                this.activeManageVendor = IntegrationVendor.TOPAL;
            });

        // Accounto: same 409 choreography as Topal (the key's tenant is already connected).
        this.actions$
            .pipe(ofType(integrationActions.accountoConnectAlreadyExists), takeUntil(this.destroy$))
            .subscribe(() => {
                this.activeConnectVendor = null;
                this.manageModalInitialConnectionId = null;
                this.activeManageVendor = IntegrationVendor.ACCOUNTO;
            });

        // Proffix: same 409 choreography as Topal.
        this.actions$
            .pipe(ofType(integrationActions.proffixConnectAlreadyExists), takeUntil(this.destroy$))
            .subscribe(() => {
                this.activeConnectVendor = null;
                this.manageModalInitialConnectionId = null;
                this.activeManageVendor = IntegrationVendor.PROFFIX;
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * The universal Info button. For an Org-scope vendor (BC) this branches by state:
     *   - already has a connection → open the Manage modal (tenant overview)
     *   - nothing connected yet     → open the Connect-flow modal
     * One-shot read of the current connections decides which.
     */
    public onInfo(vendor: VendorDefinition): void {
        // Per-company vendor (Bexio): not configured here. Point the user to the
        // individual company via the redirect modal.
        if (vendor.scope === 'per-company') {
            this.activeRedirectVendor = vendor;
            return;
        }
        // Per-org vendor (BC): connected → manage, else → connect.
        this.integrationProvider
            .getConnectionsByVendor$(vendor.vendor)
            .pipe(take(1))
            .subscribe((connections) => {
                if (connections.length > 0) {
                    this.activeManageVendor = vendor.vendor;
                } else {
                    this.activeConnectVendor = vendor.vendor;
                }
            });
    }

    public onRedirectModalClosed(): void {
        this.activeRedirectVendor = null;
    }

    private deriveCardState(vendor: IntegrationVendor, connections: IIntegrationConnection[]): VendorBadgeState {
        const state: VendorBadgeState = {
            connected: connections.length > 0,
            needsAction: connections.some(
                (c) => c.status === ConnectionStatus.EXPIRED || c.status === ConnectionStatus.ERROR,
            ),
        };
        // Accounto API keys expire and nothing renews them (REV-21119 D7): warn from 30 days before,
        // error once one has expired. Only Accounto — elsewhere `tokenExpiresAt` is a refreshed token.
        if (vendor === IntegrationVendor.ACCOUNTO) {
            const expiry = worstApiKeyExpiryState(connections.map((c) => apiKeyExpiryState(c.tokenExpiresAt)));
            if (expiry === 'expired') {
                return { ...state, badgeKey: 'aumico_accounto_card_key_expired', badgeVariant: 'danger' };
            }
            if (expiry === 'expiring') {
                return { ...state, badgeKey: 'aumico_accounto_card_key_expiring', badgeVariant: 'action' };
            }
        }
        return state;
    }

    public onConnectModalClosed(): void {
        this.activeConnectVendor = null;
        this.integrationProvider.cancelSetup();
    }

    public onManageModalClosed(): void {
        this.activeManageVendor = null;
        this.manageModalInitialConnectionId = null;
    }

    /**
     * The manage modal asked us to spawn the connect modal so the user can add
     * a new tenant. We keep the manage modal open in the background so that
     * after the connect flow resolves (or is cancelled) the user lands back in
     * the tenant list.
     *
     * NOTE: ham-modal lifts itself into a single global placeholder, so two
     * modals open at the same time will visually stack. Acceptable for now —
     * once the connect modal closes the manage modal is in front again.
     */
    public onAddTenantRequested(vendor: IntegrationVendor): void {
        this.activeConnectVendor = vendor;
    }

    /**
     * Abacus manage modal asked to open the mapping modal for either:
     *   - a new mapping (`aumicoCompanyId === null`), or
     *   - editing an existing row's Mandanten ID.
     * The manage modal stays open behind; closing the mapping modal lands us
     * back in the detail view.
     */
    public onAbacusEditMapping(payload: { connectionId: string; aumicoCompanyId: string | null }): void {
        this.abacusMapping = payload;
    }

    public onAbacusMappingClosed(): void {
        this.abacusMapping = null;
    }

    public trackByVendor(_: number, vendor: VendorDefinition): string {
        return vendor.vendor;
    }
}
